import { useCallback, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DetailedViewOptions } from "@/components/controls/OptionsDropdownMenu/OptionsDropdownMenu";
import { LICENSE_TYPE_ID, useProducts } from "@/entities/settings/hooks";
import { CreateRevenueItemRequest } from "@/pages/contracts/hooks";
import { SECTIONS } from "@/pages/settings/pages/products-v2/consts";
import { useServiceItemTypes } from "@/pages/settings/pages/products-v2/hooks";
import {
  BillingFrequency,
  ServiceDuration,
  ServiceItem
} from "@/pages/settings/pages/products-v2/types";
import {
  calculateServiceEndDate,
  hasConfiguredPricing
} from "@/pages/deals/components/commercials/DealCommercialsV2/ContractTermsV2/utils";
import {
  estimateDealFinancials,
  getEffectivePrice,
  getLineFieldVisibility,
  getLineTotal,
  getSelectedPricing,
  isFlatRateService,
  isFlexibleService,
  isLineComplete
} from "./financials";
import { DealServiceLine, FLEXIBLE_TERM_ID } from "./types";

const createLine = (key: string, service: ServiceItem): DealServiceLine => {
  const defaultPricingId = service.pricing[0]?.id ?? "";

  return {
    key,
    service,
    pricingId: isFlexibleService(service) ? FLEXIBLE_TERM_ID : defaultPricingId,
    quantity: 1,
    duration: ServiceDuration.ONE_YEAR,
    billingFrequency: BillingFrequency.MONTHLY,
    price: 0
  };
};

export const useDealServiceLines = (productId: string) => {
  const { t } = useTranslation();
  const { data: productsData, isLoading: isLoadingProducts } = useProducts();
  const { data: serviceItemTypes = [] } = useServiceItemTypes();
  const [lines, setLines] = useState<DealServiceLine[]>([]);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const nextKey = useRef(0);

  const product = useMemo(
    () =>
      productId
        ? productsData?.products?.find(
            (candidate) => candidate.id === productId
          )
        : undefined,
    [productId, productsData]
  );

  const availableServices = useMemo(
    () =>
      ((product?.serviceItems ?? []) as ServiceItem[]).filter(
        hasConfiguredPricing
      ),
    [product]
  );

  const addLine = useCallback((service: ServiceItem) => {
    const key = `line-${nextKey.current++}`;
    setLines((previous) => [...previous, createLine(key, service)]);
    setExpandedKey(key);
  }, []);

  const updateLine = useCallback(
    (key: string, changes: Partial<DealServiceLine>) => {
      setLines((previous) =>
        previous.map((line) =>
          line.key === key ? { ...line, ...changes } : line
        )
      );
    },
    []
  );

  const removeLine = useCallback((key: string) => {
    setLines((previous) => previous.filter((line) => line.key !== key));
    setExpandedKey((previous) => (previous === key ? null : previous));
  }, []);

  const reset = useCallback(() => {
    setLines([]);
    setExpandedKey(null);
  }, []);

  const serviceOptions: DetailedViewOptions = useMemo(() => {
    const options: DetailedViewOptions = {};
    availableServices.forEach((service) => {
      options[service.id] = {
        action: () => addLine(service),
        customText: service.name,
        section: service.typeId === LICENSE_TYPE_ID ? "1" : "2",
        secondaryText:
          serviceItemTypes.find((type) => type.id === service.typeId)?.name ||
          String(service.typeId)
      };
    });
    return options;
  }, [availableServices, serviceItemTypes, addLine]);

  const sections = useMemo(
    () =>
      SECTIONS.map((section) => ({
        id: section.id.toString(),
        title: t(section.name)
      })),
    [t]
  );

  const estimate = useMemo(() => estimateDealFinancials(lines), [lines]);

  const areLinesValid = useMemo(
    () => lines.length > 0 && lines.every(isLineComplete),
    [lines]
  );

  // The deal's expected close date becomes every service's start date.
  const buildRevenueItemRequests = useCallback(
    (serviceStartDate: string | null): CreateRevenueItemRequest[] =>
      lines.map((line) => {
        const visibility = getLineFieldVisibility(line);
        const pricing = getSelectedPricing(line);
        const isFlexible = isFlexibleService(line.service);
        // Only rendered inputs are the user's intent; the rest come from the tier.
        const duration = visibility.duration
          ? line.duration
          : (pricing?.serviceItemPeriods ?? null);
        const billingFrequency = visibility.billingFrequency
          ? line.billingFrequency
          : (pricing?.billingFrequency ?? null);

        return {
          serviceItemId: line.service.id,
          serviceItemName: line.service.name,
          serviceItemPricingId: isFlexible ? null : (pricing?.id ?? null),
          serviceStartDate,
          serviceEndDate: serviceStartDate
            ? calculateServiceEndDate(serviceStartDate, duration)
            : null,
          autoRenewal: false,
          quantity: isFlatRateService(line.service) ? null : line.quantity,
          pricePerUnit: isFlatRateService(line.service)
            ? null
            : getEffectivePrice(line),
          isDiscountPercentage: true,
          discountAmount: 0,
          totalLocal: getLineTotal(line),
          billingStartDate: serviceStartDate,
          billingEndDate: null,
          customBillingFrequency: billingFrequency,
          customServiceDuration: duration,
          customOptOutDate: null,
          eqPaymentsNumOfPayments: null,
          eqPaymentsBillingFrequency: null,
          // This flow has no payment-schedule editor.
          scheduledPayments: []
        };
      }),
    [lines]
  );

  return {
    lines,
    expandedKey,
    setExpandedKey,
    updateLine,
    removeLine,
    reset,
    serviceOptions,
    sections,
    productName: product?.name ?? "",
    isLoadingProducts,
    hasAvailableServices: availableServices.length > 0,
    estimate,
    areLinesValid,
    buildRevenueItemRequests
  };
};
